import { useMemo, useState } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { businessesCollection, modulePresetsCollection, eq } from "@cliqsuite/db";
import { useAuth } from "../../lib/auth";
import { useStoreScope } from "../../lib/storeScope";
import { Modal } from "../common/Modal";
import { PrintHubDownloads } from "../settings/PrintHubDownloads";

interface AppLink {
  key: string;
  name: string;
  desc: string;
  icon: string;
  url: string;
}

/**
 * Quick-launch links for cliqsuite.app's other live apps — filtered to
 * whatever this business's platform (pos vs service, from its business_type
 * preset — see routes/modules.tsx's MODULE_PLATFORM) and this store's
 * enabled modules actually support, so a salon owner never sees a "Kitchen
 * Display" link and a restaurant owner never sees "Service Register". Each
 * app's URL is baked in at build time (see docker-compose.prod.yml/
 * DEPLOY.md) — a link is simply omitted if its URL was never configured for
 * this deployment, same convention as PUBLIC_GUEST_PORTAL_URL elsewhere.
 *
 * Lives in the Sidebar (between the nav list and the account card) rather
 * than at the top of Overview, where it used to push the day's actual
 * numbers below the fold on every single visit.
 */
export function AppLinksSection({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const [printHubOpen, setPrintHubOpen] = useState(false);
  const { user } = useAuth();
  const { store } = useStoreScope();
  const businessId = user?.business_id;

  const { data: businesses } = useLiveQuery(
    (q) =>
      businessId
        ? q.from({ b: businessesCollection }).where(({ b }) => eq(b.id, businessId))
        : q.from({ b: businessesCollection }).where(({ b }) => eq(b.id, "__none__")),
    [businessId],
  );
  const { data: presetRows } = useLiveQuery((q) => q.from({ p: modulePresetsCollection }));

  const business = businesses?.[0];
  const preset = (presetRows ?? []).find((p) => p.key === business?.business_type);
  const platform = preset?.platform ?? "pos";
  const installed = business?.installed_modules ?? [];

  const posUrl = (import.meta.env.VITE_POS_URL as string | undefined) || "";
  const kitchenUrl = (import.meta.env.VITE_KITCHEN_DISPLAY_URL as string | undefined) || "";
  const serviceUrl = (import.meta.env.VITE_SERVICE_URL as string | undefined) || "";
  const guestUrl = (import.meta.env.VITE_GUEST_PORTAL_URL as string | undefined) || "";

  const links = useMemo<AppLink[]>(() => {
    const out: AppLink[] = [];
    if (platform === "pos" && posUrl) {
      out.push({ key: "pos", name: "Point of Sale", desc: "The till — cart, checkout & receipts", icon: "point_of_sale", url: posUrl });
    }
    if (platform === "service" && serviceUrl) {
      out.push({ key: "service", name: "Service Register", desc: "Appointments, walk-ins & checkout", icon: "event_available", url: serviceUrl });
    }
    if (platform === "pos" && installed.includes("restaurant") && kitchenUrl) {
      out.push({ key: "kitchen", name: "Kitchen Display", desc: "Live prep tickets for the kitchen", icon: "soup_kitchen", url: kitchenUrl });
    }
    if (store?.guest_portal_enabled && guestUrl) {
      out.push({ key: "guest", name: "Guest Portal", desc: "Customer self-ordering by QR code", icon: "qr_code_2", url: guestUrl });
    }
    return out;
  }, [platform, installed, store?.guest_portal_enabled, posUrl, serviceUrl, kitchenUrl, guestUrl]);

  return (
    <>
      <button type="button" className={`o-nav-head${open ? " is-open" : ""}`} aria-expanded={open} onClick={onToggle}>
        <span className="ms" aria-hidden="true">apps</span>
        <span style={{ flex: 1, textAlign: "left" }}>Your apps</span>
        <span className="ms o-nav-head__chev" aria-hidden="true">{open ? "expand_more" : "chevron_right"}</span>
      </button>
      {open && (
        <div className="o-nav__group o-nav__group--sub">
          {links.map((link) => (
            <a className="o-nav-item" key={link.key} href={link.url} target="_blank" rel="noopener noreferrer" title={link.desc}>
              <span className="ms" aria-hidden="true">{link.icon}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{link.name}</span>
              <span className="ms" aria-hidden="true" style={{ fontSize: 16 }}>open_in_new</span>
            </a>
          ))}
          <button type="button" className="o-nav-item" title="Download the local receipt-printing companion app" onClick={() => setPrintHubOpen(true)}>
            <span className="ms" aria-hidden="true">print</span>
            <span style={{ flex: 1, textAlign: "left" }}>Print Hub app</span>
            <span className="ms" aria-hidden="true" style={{ fontSize: 16 }}>download</span>
          </button>
        </div>
      )}
      {printHubOpen && (
        <Modal title="Print Hub app" width={620} onClose={() => setPrintHubOpen(false)}>
          <PrintHubDownloads />
        </Modal>
      )}
    </>
  );
}
